import { Component, Input } from '@angular/core';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';

// A cell of the wheel: one hue sector at one saturation step.
interface SatCell {
  d: string;     // the annular-sector SVG path
  hex: string;   // the cell's colour
  hue: number;   // 0..360, the sector's hue
  sat: number;   // 0..100, the step's saturation
  ring: number;  // 0 outermost (most saturated) .. levels-1 (most muted)
  sector: number;
  cx: number;    // the cell's centroid, so the picked cell can grow in place
  cy: number;
}

// Interactive "saturation wheel" for the Saturation post. The ring is split into
// hue sectors and concentric saturation steps: the outer band is the pure hue and
// each step inward drains saturation toward grey, with lightness held constant so
// the move is saturation only (the post's whole point). Click any cell to read its
// hue name, saturation and hex; the centre disc echoes the saturation. The post
// marks the mount point with <!--SAT-START-->...<!--SAT-END--> around a static
// fallback; BlogPostPageComponent swaps this live component in. Built in the same
// design language as the color-wheel widget.
@Component( {
  selector: 'app-saturation-wheel',
  templateUrl: './saturation-wheel.component.html',
  styleUrls: [ './saturation-wheel.component.scss' ],
} )
export class SaturationWheelComponent {

  @Input() heading = 'Try It Yourself';

  // Geometry (260-unit viewBox, centre 130). Outer rim RO, inner hole RI; the band
  // between them is split into LEVELS saturation steps, the circle into SECTORS hues.
  private readonly CX = 130;
  private readonly RO = 120;
  private readonly RI = 44;
  readonly levels = 4;
  readonly sectors = 12;

  // Saturation steps in clean quarters from the outer ring inward: 100, 75, 50, 25.
  // Lightness is held constant across the wheel so only saturation changes from step to
  // step. It is taken from the post color (via [hex]) so the wheel sits at the post's
  // own lightness and the post color can be an exact cell.
  light = 56;

  // Per sector fill hue (geometry stays an even 30 degrees; one sector's hue is snapped
  // to the post hue so the post color lands on a cell). Built from [hex].
  hues: number[] = [];

  cells: SatCell[] = [];
  selected!: SatCell;

  private _hex = '#13B5A6';
  private selRing = 1;
  private selSector = 0;
  private tracked = false;

  // The post passes its color here ([hex]="post.relatedColor"); the wheel opens on it.
  @Input() set hex( value: string ) {
    const rgb = this.parseHex( value );
    if ( !rgb ) {
      return;
    }
    this._hex = '#' + String( value ).replace( /^#/, '' ).toUpperCase();
    this.applyHex( rgb );
  }
  get hex(): string {
    return this._hex;
  }

  constructor( private analyticsService: AnalyticsService ) {
    this.applyHex( this.parseHex( this._hex )! );
  }

  // Derive the wheel's lightness, the snapped sector hue and the starting cell from the
  // post color, then (re)build.
  private applyHex( rgb: [ number, number, number ] ): void {
    const [ h, s, l ] = this.rgbToHsl( rgb );
    this.light = l;

    const step = 360 / this.sectors;
    const hues: number[] = [];
    for ( let j = 0; j < this.sectors; j++ ) {
      hues.push( j * step );
    }
    // Snap the nearest sector's fill hue to the exact post hue (red stays at the top,
    // sectors stay evenly spaced; only this one sector's color shifts a few degrees).
    let snap = 0, best = Infinity;
    for ( let j = 0; j < this.sectors; j++ ) {
      const d = Math.min( Math.abs( hues[ j ] - h ), 360 - Math.abs( hues[ j ] - h ) );
      if ( d < best ) { best = d; snap = j; }
    }
    hues[ snap ] = h;
    this.hues = hues;
    this.selSector = snap;

    // Saturation ring nearest the post saturation (rings are clean quarters 100/75/50/25).
    let ri = 0, rb = Infinity;
    for ( let i = 0; i < this.levels; i++ ) {
      const ringSat = 100 - i * ( 100 / this.levels );
      const d = Math.abs( ringSat - s );
      if ( d < rb ) { rb = d; ri = i; }
    }
    this.selRing = ri;

    this.cells = this.build();
    this.selected = this.cells[ this.selRing * this.sectors + this.selSector ];
  }

  get centerHex(): string {
    return this.selected.hex;
  }

  // The centre saturation label flips to dark on light cells and white on dark ones,
  // so it stays readable across the whole wheel.
  get centerDark(): boolean {
    const h = this.selected.hex.replace( '#', '' );
    const r = parseInt( h.slice( 0, 2 ), 16 );
    const g = parseInt( h.slice( 2, 4 ), 16 );
    const b = parseInt( h.slice( 4, 6 ), 16 );
    return ( 0.299 * r + 0.587 * g + 0.114 * b ) > 150;
  }

  select( cell: SatCell ): void {
    this.selected = cell;
    this.track();
  }

  // ---- build the grid ---------------------------------------------------------

  private build(): SatCell[] {
    const cells: SatCell[] = [];
    const step = 360 / this.sectors;
    const thick = ( this.RO - this.RI ) / this.levels;
    for ( let i = 0; i < this.levels; i++ ) {
      const ro = this.RO - i * thick;
      const ri = ro - thick;
      const rmid = ( ri + ro ) / 2;
      const sat = 100 - i * ( 100 / this.levels );
      for ( let j = 0; j < this.sectors; j++ ) {
        const ang = j * step;                 // even angular position (geometry)
        const hue = this.hues[ j ];           // the sector's fill hue (one snapped to post)
        const a0 = ang - step / 2;
        const a1 = ang + step / 2;
        const [ cx, cy ] = this.point( rmid, ang );
        cells.push( {
          d: this.sectorPath( ri, ro, a0, a1 ),
          hex: this.hslToHex( hue, sat, this.light ),
          hue,
          sat,
          ring: i,
          sector: j,
          cx,
          cy,
        } );
      }
    }
    return cells;
  }

  // A point on the wheel at radius r and angle deg, measured from the top
  // (12 o'clock = 0 = red) going clockwise, matching the hue ring elsewhere.
  private point( r: number, deg: number ): [ number, number ] {
    const a = deg * Math.PI / 180;
    return [
      +( this.CX + r * Math.sin( a ) ).toFixed( 2 ),
      +( this.CX - r * Math.cos( a ) ).toFixed( 2 ),
    ];
  }

  // One annular sector (a donut segment): along the outer arc, in to the inner arc,
  // back along it, and closed. Each sector spans < 180 degrees so large-arc is 0.
  private sectorPath( ri: number, ro: number, a0: number, a1: number ): string {
    const os = this.point( ro, a0 );
    const oe = this.point( ro, a1 );
    const ie = this.point( ri, a1 );
    const is = this.point( ri, a0 );
    return `M${ os[ 0 ] } ${ os[ 1 ] }` +
      `A${ ro } ${ ro } 0 0 1 ${ oe[ 0 ] } ${ oe[ 1 ] }` +
      `L${ ie[ 0 ] } ${ ie[ 1 ] }` +
      `A${ ri } ${ ri } 0 0 0 ${ is[ 0 ] } ${ is[ 1 ] }Z`;
  }

  private track(): void {
    if ( this.tracked ) {
      return;
    }
    this.tracked = true;
    this.analyticsService.logEvent( 'Blog Widget Used', { widget: 'saturation-wheel' } );
  }

  private parseHex( text: string ): [ number, number, number ] | null {
    const h = String( text || '' ).trim().replace( /^#/, '' );
    if ( !/^[0-9a-fA-F]{6}$/.test( h ) ) {
      return null;
    }
    return [
      parseInt( h.slice( 0, 2 ), 16 ),
      parseInt( h.slice( 2, 4 ), 16 ),
      parseInt( h.slice( 4, 6 ), 16 ),
    ];
  }

  // [r,g,b] (0..255) to [hue 0..360, saturation 0..100, lightness 0..100].
  private rgbToHsl( rgb: [ number, number, number ] ): [ number, number, number ] {
    const r = rgb[ 0 ] / 255, g = rgb[ 1 ] / 255, b = rgb[ 2 ] / 255;
    const mx = Math.max( r, g, b ), mn = Math.min( r, g, b ), d = mx - mn;
    const l = ( mx + mn ) / 2;
    let h = 0, s = 0;
    if ( d !== 0 ) {
      s = d / ( 1 - Math.abs( 2 * l - 1 ) );
      if ( mx === r ) {
        h = ( ( g - b ) / d ) % 6;
      } else if ( mx === g ) {
        h = ( b - r ) / d + 2;
      } else {
        h = ( r - g ) / d + 4;
      }
      h = ( h * 60 + 360 ) % 360;
    }
    return [ h, s * 100, l * 100 ];
  }

  private hslToHex( h: number, s: number, l: number ): string {
    s /= 100;
    l /= 100;
    const c = ( 1 - Math.abs( 2 * l - 1 ) ) * s;
    const x = c * ( 1 - Math.abs( ( ( h / 60 ) % 2 ) - 1 ) );
    const m = l - c / 2;
    let r = 0, g = 0, b = 0;
    if ( h < 60 ) { r = c; g = x; }
    else if ( h < 120 ) { r = x; g = c; }
    else if ( h < 180 ) { g = c; b = x; }
    else if ( h < 240 ) { g = x; b = c; }
    else if ( h < 300 ) { r = x; b = c; }
    else { r = c; b = x; }
    const to = ( v: number ) => Math.round( ( v + m ) * 255 ).toString( 16 ).padStart( 2, '0' );
    return ( '#' + to( r ) + to( g ) + to( b ) ).toUpperCase();
  }
}
